import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AssetExportConfigurationContext } from '../../../../../../context/AssetExportConfigurationContext';
import AssetExportConfigurationForm, {
  DraggableAssetExportFieldIdentifier,
} from '../Common/AssetExportConfigurationForm';
import { Routing } from '../../../../../../util/routes.util';
import {
  AssetExportConfiguration,
  AssetExportConfigurationField,
  AssetExportConfigurationInput,
  UpdateAssetExportConfigurationInput,
} from '../../../../../../types/asset_export_configuration';
import UpdateButton from '../../../../../components/Button/templates/UpdateButton';
import { BackButton, ButtonVariant, DeleteButton } from '../../../../../components/Button';
import { DraggableItemData } from '../../../../Common/DraggableItem';
import { useRequest } from '../../../../../../requests/common/requestHook';
import { AssetExportConfigurationRequest } from '../../../../../../requests/assetExportConfiguration.request';
import DeleteAssetExportConfigurationModal from './Modals/DeleteAssetExportConfigurationModal';
import { CompanyContext } from '../../../../../../context/CompanyContext';

interface UpdateAssetExportConfigurationPaneProps {}

export default function UpdateAssetExportConfigurationPane({}: UpdateAssetExportConfigurationPaneProps) {
  const id = useParams()['id'] || '';
  if (!id) return null;

  const navigate = useNavigate();

  const { currentCompany } = useContext(CompanyContext);
  const { assetExportConfigurations, setAssetExportConfigurations, assetExportConfigurationsLoading } = useContext(
    AssetExportConfigurationContext,
  );

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const existing = assetExportConfigurations.get(id);
  const [input, setInput] = useState<AssetExportConfigurationInput>(
    new AssetExportConfigurationInput(existing || AssetExportConfiguration.default(currentCompany.id)),
  );

  useEffect(() => {
    if (!assetExportConfigurationsLoading && existing) {
      setInput(new AssetExportConfigurationInput(existing));
    }
  }, [assetExportConfigurationsLoading]);

  const [update, { loading: updateLoading }] = useRequest(AssetExportConfigurationRequest.update);

  const handleUpdate = async (
    configuration: UpdateAssetExportConfigurationInput,
    items: DraggableAssetExportFieldIdentifier[],
  ) => {
    configuration.mapping = DraggableItemData.toMap<AssetExportConfigurationField>(items);

    const result = await update({ data: [new AssetExportConfigurationInput(configuration).forUpdate()] });
    if (!result) return;

    const config = result[0];
    assetExportConfigurations.set(config.id, new AssetExportConfiguration(config));
    setAssetExportConfigurations(new Map(assetExportConfigurations));
    navigate(Routing.assets.settings.report.overview());
  };

  const footer = (configuration: AssetExportConfigurationInput, items: DraggableAssetExportFieldIdentifier[]) => {
    return (
      <div className='flex justify-between'>
        <div>
          <DeleteButton
            disabled={assetExportConfigurationsLoading || updateLoading}
            onClick={() => setOpenDeleteModal(true)}
          />
        </div>
        <div className='flex items-center gap-2'>
          <BackButton
            disabled={updateLoading}
            variant={ButtonVariant.secondary}
            onClick={() => navigate(Routing.assets.settings.report.overview())}
          />
          <UpdateButton
            loading={updateLoading}
            disabled={assetExportConfigurationsLoading || !configuration.filename || !configuration.name}
            onClick={() => handleUpdate(configuration, items)}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <DeleteAssetExportConfigurationModal open={openDeleteModal} setOpen={setOpenDeleteModal} ids={new Set([id])} />
      <AssetExportConfigurationForm
        loading={assetExportConfigurationsLoading}
        input={input}
        setInput={setInput}
        footer={footer}
      />
    </>
  );
}
